/**
 * Maps Firebase Auth error codes to human-readable messages.
 *
 * Firebase throws errors with codes like `auth/user-not-found` and exposes the
 * raw `Firebase: Error (auth/user-not-found).` text on the `.message`
 * property. Surfacing that string to users is jarring — this helper turns it
 * into something people can act on.
 *
 * Usage:
 *   try {
 *     await signInWithEmailAndPassword(auth, email, password)
 *   } catch (e) {
 *     store.commit('setError', friendlyAuthError(e))
 *   }
 *
 * Coverage notes:
 * - We treat the modern `auth/invalid-credential` (Firebase's unified
 *   wrong-email / wrong-password code as of 2024+) the same way as the
 *   legacy `auth/wrong-password` and `auth/user-not-found` — a single
 *   "Email or password is incorrect" message that doesn't reveal whether
 *   the email exists.
 * - For password-reset flows we never want to leak existence either, so
 *   the caller should map success and "user-not-found" to the same notice
 *   ("If an account exists, we've sent a reset link"). This helper still
 *   returns a sensible message for that code if a caller chooses to use
 *   it directly.
 * - Some codes are SSO-popup specific (popup-closed-by-user etc.). We map
 *   those to "Sign-in was cancelled" rather than reading as a hard error
 *   because the user almost certainly intended to dismiss it.
 *
 * Reference: https://firebase.google.com/docs/auth/admin/errors
 */

import { i18n } from '../../i18n';

const t = (key: string): string => i18n.global.t(key);

type MaybeFirebaseError = {
  code?: unknown;
  message?: unknown;
  customData?: unknown;
};

const FRIENDLY_MESSAGES: Record<string, () => string> = {
  // ----- Email / password sign-in --------------------------------------
  // Modern unified code — covers wrong email AND wrong password without
  // revealing which one was wrong.
  'auth/invalid-credential': () => t('utils.auth_errors.incorrect_credentials'),
  'auth/invalid-login-credentials': () => t('utils.auth_errors.incorrect_credentials'),
  'auth/wrong-password': () => t('utils.auth_errors.incorrect_credentials'),
  'auth/user-not-found': () => t('utils.auth_errors.incorrect_credentials'),
  'auth/invalid-password': () => t('utils.auth_errors.incorrect_credentials'),

  // ----- Account state -------------------------------------------------
  'auth/user-disabled': () => t('utils.auth_errors.account_disabled'),
  'auth/user-token-expired': () => t('utils.auth_errors.session_expired'),
  'auth/requires-recent-login': () => t('utils.auth_errors.requires_recent_login'),
  'auth/user-mismatch': () => t('utils.auth_errors.user_mismatch'),

  // ----- Sign up --------------------------------------------------------
  'auth/email-already-in-use': () => t('utils.auth_errors.email_already_in_use'),
  'auth/email-already-exists': () => t('utils.auth_errors.email_already_in_use'),
  'auth/credential-already-in-use': () => t('utils.auth_errors.credential_already_in_use'),
  'auth/account-exists-with-different-credential':
    () => t('utils.auth_errors.account_exists_different_method'),

  // ----- Validation ----------------------------------------------------
  'auth/invalid-email': () => t('utils.auth_errors.invalid_email'),
  'auth/missing-email': () => t('utils.auth_errors.missing_email'),
  'auth/missing-password': () => t('utils.auth_errors.missing_password'),
  'auth/weak-password': () => t('utils.auth_errors.weak_password'),

  // ----- Rate limiting / availability ----------------------------------
  'auth/too-many-requests': () => t('utils.auth_errors.too_many_requests'),
  'auth/quota-exceeded': () => t('utils.auth_errors.quota_exceeded'),
  'auth/network-request-failed': () => t('utils.auth_errors.network_request_failed'),
  'auth/timeout': () => t('utils.auth_errors.timeout'),
  'auth/internal-error': () => t('utils.auth_errors.internal_error'),

  // ----- SSO popups ----------------------------------------------------
  'auth/popup-closed-by-user': () => t('utils.auth_errors.sign_in_cancelled'),
  'auth/cancelled-popup-request': () => t('utils.auth_errors.sign_in_cancelled_try_again'),
  'auth/popup-blocked': () => t('utils.auth_errors.popup_blocked'),
  'auth/user-cancelled': () => t('utils.auth_errors.sign_in_cancelled'),
  'auth/redirect-cancelled-by-user': () => t('utils.auth_errors.sign_in_cancelled'),
  'auth/redirect-operation-pending': () => t('utils.auth_errors.redirect_operation_pending'),
  'auth/web-storage-unsupported':
    () => t('utils.auth_errors.web_storage_unsupported'),
  'auth/operation-not-supported-in-this-environment':
    () => t('utils.auth_errors.operation_not_supported'),

  // ----- Domain / config ------------------------------------------------
  'auth/unauthorized-domain':
    () => t('utils.auth_errors.unauthorized_domain'),
  'auth/unauthorized-continue-uri':
    () => t('utils.auth_errors.unauthorized_continue_uri'),
  'auth/operation-not-allowed':
    () => t('utils.auth_errors.operation_not_allowed'),
  'auth/admin-restricted-operation': () => t('utils.auth_errors.admin_restricted_operation'),

  // ----- Email-link / magic-link / reset --------------------------------
  'auth/expired-action-code': () => t('utils.auth_errors.expired_action_code'),
  'auth/invalid-action-code': () => t('utils.auth_errors.invalid_action_code'),
  'auth/missing-action-code': () => t('utils.auth_errors.missing_action_code'),
  'auth/unverified-email': () => t('utils.auth_errors.unverified_email'),

  // ----- MFA / verification --------------------------------------------
  'auth/multi-factor-auth-required': () => t('utils.auth_errors.mfa_required'),
  'auth/invalid-verification-code': () => t('utils.auth_errors.invalid_verification_code'),
  'auth/invalid-verification-id': () => t('utils.auth_errors.invalid_verification_id'),
  'auth/missing-verification-code': () => t('utils.auth_errors.missing_verification_code'),
  'auth/missing-verification-id': () => t('utils.auth_errors.missing_verification_id'),
  'auth/maximum-second-factor-count-exceeded':
    () => t('utils.auth_errors.max_second_factors'),

  // ----- Misc -----------------------------------------------------------
  'auth/captcha-check-failed': () => t('utils.auth_errors.captcha_check_failed'),
  'auth/no-such-provider': () => t('utils.auth_errors.no_such_provider'),
};

const defaultFallback = (): string => t('utils.auth_errors.default_fallback');

/**
 * Strip Firebase's `Firebase: Error (auth/...)` wrapper from a raw message.
 * Used as a last resort when the error has no `.code` we recognise but still
 * has a Firebase-shaped message string.
 */
function extractCode(rawMessage: string): string | null {
  const match = rawMessage.match(/auth\/[a-z0-9-]+/i);
  return match ? match[0].toLowerCase() : null;
}

export function friendlyAuthError(error: unknown, fallback: string = defaultFallback()): string {
  if (!error) return fallback;

  const err = error as MaybeFirebaseError;

  // Preferred path: structured Firebase error with a `.code`.
  if (typeof err.code === 'string' && FRIENDLY_MESSAGES[err.code]) {
    return FRIENDLY_MESSAGES[err.code]();
  }

  // Fallback: try to recover the code from a raw "Firebase: Error (auth/...)" string.
  if (typeof err.message === 'string') {
    const recovered = extractCode(err.message);
    if (recovered && FRIENDLY_MESSAGES[recovered]) {
      return FRIENDLY_MESSAGES[recovered]();
    }
  }

  // Last resort — known unrecognised Firebase error shape: hide the raw
  // `Firebase: Error (...)` wrapper and surface our generic fallback.
  if (typeof err.message === 'string' && /^Firebase:/i.test(err.message)) {
    return fallback;
  }

  // Plain-string errors that don't look like Firebase output can pass through
  // unmodified — they're likely intentional human-readable messages from
  // upstream code.
  if (typeof error === 'string') return error;
  if (typeof err.message === 'string' && err.message.length > 0) return err.message;

  return fallback;
}
